<template>
  <div class="vv">
    <div class="vv-head">
      <div class="vv-icon">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <div class="vv-head-text">
        <div class="vv-title-row">
          <h1 class="vv-title">Cast Statistics</h1>
          <span class="vv-locked-pill">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            LOCKED
          </span>
        </div>
        <p class="vv-sub">
          Host-controlled access. Verify to view transcripts, recordings,
          insights & shorts.
        </p>
      </div>
    </div>

    <div class="vv-policy">
      <div class="vv-policy-label">HOST ACCESS POLICY</div>
      <div class="vv-policy-row">
        <span class="vv-policy-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <polyline points="9 12 11 14 15 10" />
          </svg>
        </span>
        <div class="vv-policy-text">
          <div class="vv-policy-title">
            Private. Verifiable Credentials required
          </div>
          <div class="vv-policy-desc">
            Access restricted to holders of credentials approved by the host
            (e.g. accredited press, org membership).
          </div>
        </div>
      </div>
    </div>

    <button class="vv-back" type="button" @click="$emit('back')">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
      </svg>
      Back
    </button>

    <h3 class="vv-required">Required credentials</h3>
    <p class="vv-required-desc">
      The host requires the following VCs. You'll present a zero-knowledge
      proof. Issuer signature only, no personal data.
    </p>

    <div class="vv-credentials">
      <div
        v-for="c in credentials"
        :key="c.id"
        class="vv-cred"
        :class="{ found: c.found }"
      >
        <span class="vv-cred-icon">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <polyline points="9 12 11 14 15 10" />
          </svg>
        </span>
        <div class="vv-cred-body">
          <div class="vv-cred-name">{{ c.name }}</div>
          <div class="vv-cred-issuer">Issued by {{ c.issuer }}</div>
        </div>
        <span
          class="vv-cred-status"
          :class="c.found ? 'found' : 'missing'"
        >{{ c.found ? 'FOUND IN WALLET' : 'NOT FOUND' }}</span>
      </div>
    </div>

    <div class="vv-share">
      <strong>What you'll share:</strong>
      a signed proof that you hold {{ credentials.length === 1 ? 'this credential' : 'both credentials' }}.
      Your name, email and DID stay hidden from the host.
    </div>

    <button
      class="vv-present"
      type="button"
      :disabled="!allFound || presenting"
      @click="present"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
        <path d="M9 10v2" />
        <path d="M15 10v2" />
        <path d="M6 12a6 6 0 0 0 12 0" />
      </svg>
      {{ presenting ? 'Presenting…' : 'Present credentials' }}
    </button>
  </div>
</template>

<script>
const DEFAULT_CREDENTIALS = [
  {
    id: 'press',
    name: 'Accredited Press Member',
    issuer: 'Press Council',
    found: true,
  },
  {
    id: 'reporter',
    name: 'Decast Verified Reporter',
    issuer: 'Decast Trust System',
    found: true,
  },
];

export default {
  name: 'VerifyView',
  props: {
    credentials: {
      type: Array,
      default: () => DEFAULT_CREDENTIALS,
    },
  },
  data() {
    return { presenting: false };
  },
  computed: {
    allFound() {
      return this.credentials.every((c) => c.found);
    },
  },
  methods: {
    present() {
      if (!this.allFound) return;
      this.presenting = true;
      this.$emit('present', () => {
        this.presenting = false;
      });
    },
  },
};
</script>

<style scoped>
.vv {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: #fff;
}
.vv-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}
.vv-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  flex-shrink: 0;
}
.vv-head-text {
  flex: 1;
  min-width: 0;
}
.vv-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.vv-title {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.2;
  overflow-wrap: break-word;
  word-wrap: break-word;
}
.vv-locked-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.vv-sub {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.55);
  margin: 6px 0 0;
  line-height: 1.55;
}

.vv-policy {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 24px;
}
.vv-policy-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  margin-bottom: 12px;
}
.vv-policy-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.vv-policy-icon {
  color: #a78bfa;
  margin-top: 2px;
  flex-shrink: 0;
}
.vv-policy-text {
  flex: 1;
  min-width: 0;
}
.vv-policy-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 6px;
}
.vv-policy-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
}

.vv-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 18px;
  font-family: inherit;
  transition: color 0.15s ease;
}
.vv-back:hover {
  color: #fff;
}

.vv-required {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 6px;
}
.vv-required-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 18px;
  line-height: 1.5;
}

.vv-credentials {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.vv-cred {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  flex-wrap: wrap;
}
.vv-cred.found {
  background: rgba(211, 202, 87, 0.04);
  border-color: rgba(211, 202, 87, 0.25);
}
.vv-cred-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(211, 202, 87, 0.1);
  color: #d3ca57;
  flex-shrink: 0;
}
.vv-cred-body {
  flex: 1;
  min-width: 0;
}
.vv-cred-name {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 2px;
}
.vv-cred-issuer {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}
.vv-cred-status {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  padding: 5px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  flex-shrink: 0;
}
.vv-cred-status.found {
  background: rgba(211, 202, 87, 0.1);
  color: #d3ca57;
  border: 1px solid rgba(211, 202, 87, 0.4);
}
.vv-cred-status.missing {
  background: rgba(248, 113, 113, 0.08);
  color: #f87171;
  border: 1px solid rgba(248, 113, 113, 0.3);
}

.vv-share {
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.55;
  margin-bottom: 22px;
}
.vv-share strong {
  color: #fff;
  font-weight: 700;
}

.vv-present {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px 22px;
  background: #d3ca57;
  color: #0b0c14;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  box-shadow: 0 0 28px rgba(211, 202, 87, 0.18);
}
.vv-present:hover:not(:disabled) {
  background: #ddd56a;
  box-shadow: 0 0 36px rgba(211, 202, 87, 0.28);
}
.vv-present:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}
@media (max-width: 540px) {
  .vv-title {
    font-size: 24px;
  }
  .vv-icon {
    width: 48px;
    height: 48px;
  }
  .vv-policy,
  .vv-cred {
    padding: 14px 16px;
  }
  .vv-cred-body {
    flex: 1 1 calc(100% - 52px);
    min-width: 0;
  }
  .vv-cred-status {
    margin-left: 52px;
  }
  .vv-present {
    font-size: 14px;
    padding: 14px 18px;
  }
}
</style>
